import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@material-ui/core/Button';
import MenuList from '@material-ui/core/MenuList';

import ChipButton from './shared/button';
import FilterChipPopover from './shared/popover';
import FilterChipMenuItem from './shared/menu-item';
import TimeRangeDialog from '@shared/dialogs/time-range-dialog';

import { useDeferredCallback } from '@hooks/utils';

import styles from './date.scss';

const CUSTOM_RANGE_VALUE = 'CUSTOM_RANGE_VALUE';

const TimeFilterChip = ({ label, timeOptions, timeValues, onChange }) => {
  const [prevValues, setPrevValues] = useState(timeValues);
  const [pendingValues, setPendingValues] = useState(timeValues);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  const selectedOption = useMemo(() => {
    return pendingValues && timeOptions.find(({value: optionValue}) => (
      optionValue.start === pendingValues.start &&
      optionValue.end === pendingValues.end
    )) || null;
  }, [timeOptions, pendingValues]);

  const [chipValues, filterActive] = useMemo(() => {
    if (pendingValues !== null) {
      if (selectedOption) {
        return [[selectedOption.label], true];
      }
      if (pendingValues.start && pendingValues.end) {
        return [[`${pendingValues.start} - ${pendingValues.end}`], true];
      }
    }
    return [[], false];
  }, [pendingValues, selectedOption]);

  const customRangeActive = !selectedOption && filterActive;

  const handleClose = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
    if (pendingValues !== timeValues) {
      if (filterActive) {
        onChange(pendingValues);
      } else {
        onChange(null);
      }
    }
  }, [timeValues, onChange, pendingValues, setAnchorEl, setOpen, filterActive]);

  const handleCustomClose = useDeferredCallback(() => {
    setCustomOpen(false);
    if (customRangeActive && pendingValues !== timeValues) {
      handleClose();
    }
  }, [timeValues, setCustomOpen, pendingValues, customRangeActive, handleClose]);

  const handleButtonClick = useCallback(event => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    if (!open) {
      setOpen(true);
    } else {
      handleClose();
    }
  }, [anchorEl, setAnchorEl, open, setOpen, handleClose]);

  const handleSelectItem = useCallback(optionValues => {
    if (pendingValues === optionValues) {
      setPendingValues(null);
    } else {
      setPendingValues(optionValues);
    }
  }, [setPendingValues, pendingValues]);

  const handleSelectCustomRange = useCallback(() => {
    if (customRangeActive) {
      setPendingValues(null);
    } else {
      setCustomOpen(true);
    }
  }, [customRangeActive]);

  const handleClearValues = useCallback(() => {
    setPendingValues(null);
  }, [setPendingValues]);

  if (timeValues !== prevValues) {
    setPendingValues(timeValues);
    setPrevValues(timeValues);
  }

  return (
    <Fragment>
      <ChipButton
        label={label}
        values={chipValues}
        onClick={handleButtonClick}
      />
      <FilterChipPopover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        classes={{
          root: styles.dateMenu
        }}
      >
        <MenuList>
          {timeOptions.map(option => (
            <FilterChipMenuItem
              key={option.label}
              option={option}
              onSelect={handleSelectItem}
              checked={option === selectedOption}
            />
          ))}
          <FilterChipMenuItem
            option={{
              label: 'Custom range',
              value: CUSTOM_RANGE_VALUE
            }}
            onSelect={handleSelectCustomRange}
            checked={customRangeActive}
          />
        </MenuList>
        <div className={styles.buttons}>
          <Button
            onClick={handleClearValues}
            disabled={chipValues.length === 0}
            classes={{
              root: styles.button,
              disabled: styles.disabledButton
            }}
          >
            Clear
          </Button>
        </div>
      </FilterChipPopover>
      <TimeRangeDialog
        defaultRange={pendingValues}
        onChange={handleSelectItem}
        onClose={handleCustomClose}
        open={customOpen}
      />
    </Fragment>
  );
};

TimeFilterChip.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  timeOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired
    })
  })),
  timeValues: PropTypes.shape({
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired
  })
};

export default memo(TimeFilterChip);
